import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useWebSocketContext } from "./WebSocketProvider";
import dayjs from "dayjs";

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const { lastMessage, sendMessage } = useWebSocketContext();
  
  // Use environment variable or default to local backend
  const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3013/api";
  const API_BASE_URL = `${API_BASE}/sab`;
  const AUTH_TOKEN = localStorage.getItem("token");

  const [dashboard, setDashboard] = useState([]);
  const [events, setEvents] = useState([]);
  const [mainboard, setMainboard] = useState([]);
  const [scrollboard, setScrollboard] = useState([]);

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: AUTH_TOKEN ? `Bearer ${AUTH_TOKEN}` : '',
    },
  });

  // ✅ Fetch initial data from API when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, eventsRes, mainboardRes, scrollboardRes] =
          await Promise.all([
            api.get(`/dashboard`),
            api.get(`/events`),
            api.get(`/mainboard`),
            api.get(`/scrollboard`),
          ]);

        // Transform data to match expected format (add 'id' field from '_id')
        const transformData = (data) => 
          data.map(item => ({ ...item, id: item._id || item.id }));

        setDashboard(transformData(dashboardRes.data));
        setEvents(transformData(eventsRes.data));
        setMainboard(transformData(mainboardRes.data));
        setScrollboard(transformData(scrollboardRes.data));

        console.log("✅ Initial data loaded from API");
        console.log("📋 Dashboard data:", dashboardRes.data);
        console.log("📋 Events data:", eventsRes.data);
        console.log("📋 Mainboard data:", mainboardRes.data);
        console.log("📋 Scrollboard data:", scrollboardRes.data);
      } catch (error) {
        console.error("❌ Failed to fetch initial data:", error);
      }
    };

    fetchData();
  }, []);

  // ✅ Handle WebSocket Messages (ONLY for updates, not initial load)
  useEffect(() => {
    if (lastMessage) {
      try {
        const data = JSON.parse(lastMessage.data);
        console.log("📩 Received WebSocket Update:", data);

        const { type, action, payload = data } = data;
       
        switch (type) {
          case "events":
            updateState(setEvents, action, payload.data);
            break;
          case "dashboard":
            updateState(setDashboard, action, payload.data);
            break;
          case "mainboard":
            updateState(setMainboard, action, payload.data);
            break;
          case "scrollboard":
            updateState(setScrollboard, action, payload.data);
            break;
          case "pong":
            console.log("pong received @ : " + dayjs().format("DD MMM YYYY hh:mm:ss"));
            break;
          case "auth":
            console.log(payload);
            break;
          default:
            console.warn("⚠️ Unknown WebSocket message type:", type);
        }
      } catch (error) {
        console.error("❌ WebSocket message parse error:", error);
      }
    }
  }, [lastMessage]);

  // Generic function to update state based on action type
  const updateState = (setState, action, payload) => {
    if (!payload) return;
    
    // Transform payload to ensure it has an 'id' field
    const transformedPayload = typeof payload === 'object' && !Array.isArray(payload)
      ? { ...payload, id: payload._id || payload.id }
      : payload;

    setState((prevData) => {
      switch (action) {
        case "create":
          return [...prevData, transformedPayload]; // Append new item
        case "update":
          return prevData.map((item) =>
            (item.id || item._id) == (transformedPayload.id || transformedPayload._id) 
              ? transformedPayload 
              : item
          ); // Replace existing item
        case "delete":
          const deleteId = typeof payload === 'string' ? payload : (payload.id || payload._id);
          return prevData.filter((item) => (item.id || item._id) !== deleteId); // Remove item
        default:
          return prevData; // Keep previous state if action is unknown
      }
    });
  };

  // ✅ CRUD Operations with API Calls
  const addItem = async (type, item) => {
    try {
      const response = await api.post(`/${type}`, item);

      const newItem = { ...item, id: response.data.id || response.data._id, _id: response.data.id || response.data._id };

      switch (type) {
        case "dashboard":
          setDashboard([...dashboard, newItem]);
          break;
        case "events":
          setEvents([...events, newItem]);
          break;
        case "mainboard":
          setMainboard([...mainboard, newItem]);
          break;
        case "scrollboard":
          setScrollboard([...scrollboard, newItem]);
          break;
        default:
          return;
      }

      sendMessage(JSON.stringify({ type, action: "create", data: newItem }));
    } catch (error) {
      console.error(`❌ Failed to add ${type}:`, error);
    }
  };

  const updateItem = async (type, id, updatedFields) => {
    try {
      const response = await api.put(`/${type}/${id}`, updatedFields);
      const updatedItem = { ...updatedFields, id, _id: id };
    
      switch (type) {
        case "dashboard":
          setDashboard(
            dashboard.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        case "events":
          setEvents(
            events.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        case "mainboard":
          setMainboard(
            mainboard.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        case "scrollboard":
          setScrollboard(
            scrollboard.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        default:
          return;
      }
      
      sendMessage(
        JSON.stringify({ type, action: "update", data: updatedItem })
      );
    } catch (error) {
      console.error(`❌ Failed to update ${type}:`, error);
    }
  };

  const deleteItem = async (type, id) => {
    try {
      await api.delete(`/${type}/${id}`);

      switch (type) {
        case "events":
          setEvents(events.filter((item) => (item.id || item._id) !== id));
          break;
        case "mainboard":
          setMainboard(mainboard.filter((item) => (item.id || item._id) !== id));
          break;
        case "scrollboard":
          setScrollboard(scrollboard.filter((item) => (item.id || item._id) !== id));
          break;
        default:
          return;
      }

      sendMessage(JSON.stringify({ type, action: "delete", data: id }));
    } catch (error) {
      console.error(`❌ Failed to delete ${type}:`, error);
    }
  };

  return (
    <DataContext.Provider
      value={{
        dashboard,
        events,
        mainboard,
        scrollboard,
        addItem,
        updateItem,
        deleteItem,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => useContext(DataContext);
